import React, { useCallback } from 'react';
import { supportLinkSlugForDeviceIntegrationType, TrackingFn } from './utils';
import { DeviceIntegrationProvider, integrationIsIOSOnly } from '@repo/utils/models/Integration';
import LinkedinModal from './Modal/LinkedIn';
import WhatsappModal from './Modal/WhatsApp';
import FacebookModal from './Modal/Facebook';
import { MessagesSetupModal, PhoneSetupModal } from './Modal/AppleDeviceIntegration';
import InterstitialModal from './Modal/Interstitial';
import { promptForUnsupportedIntegration } from './UnsupportedIntegration';
import DeviceIntegrationOption, { IOSOnlyIntegrationOption } from './DeviceIntegration';
import TwitterModal from '@components/Integrations/Modal/TwitterModal';
import LinkedInCSVModal from '@components/Integrations/Modal/LinkedInCSVModal';
import CSVModal from './Modal/CSV';
import InstagramModal from './Modal/InstagramModal';

type ActiveModal =
  | 'google'
  | 'microsoft'
  | 'facebook'
  | 'linkedin'
  | 'linkedin-csv'
  | 'twitter-platforms'
  | 'instagram'
  | 'whatsapp'
  | 'apple-phone-log'
  | 'apple-messages'
  | 'csv';

export function IntegrationModals({
  activeModal,
  closeModal,
  forceDarkMode,
  trackConnection,
  renderLocation,
}: {
  activeModal?: ActiveModal;
  closeModal: () => void;
  forceDarkMode: boolean;
  trackConnection: TrackingFn;
  renderLocation?: 'onboarding' | 'settings';
}) {
  return (
    <>
      <InterstitialModal
        open={activeModal == 'google'}
        accountType="google"
        closeModal={closeModal}
        forceDarkMode={forceDarkMode}
        trackConnection={trackConnection}
      />
      <InterstitialModal
        open={activeModal == 'microsoft'}
        accountType="microsoft"
        closeModal={closeModal}
        forceDarkMode={forceDarkMode}
        trackConnection={trackConnection}
      />
      {activeModal === 'linkedin' && (
        <LinkedinModal
          onClose={closeModal}
          renderLocation={renderLocation}
          trackConnection={trackConnection}
        />
      )}
      {activeModal === 'whatsapp' && (
        <WhatsappModal
          onClose={closeModal}
          renderLocation={renderLocation}
          trackConnection={trackConnection}
        />
      )}
      {activeModal === 'linkedin-csv' && (
        <LinkedInCSVModal onClose={closeModal} forceDarkMode={forceDarkMode} />
      )}
      {activeModal === 'facebook' && (
        <FacebookModal onClose={closeModal} forceDarkMode={forceDarkMode} />
      )}
      {activeModal === 'twitter-platforms' && (
        <TwitterModal onClose={closeModal} forceDarkMode={forceDarkMode} />
      )}
      {activeModal === 'instagram' && (
        <InstagramModal onClose={closeModal} forceDarkMode={forceDarkMode} />
      )}
      {activeModal === 'csv' && (
        <CSVModal
          onClose={closeModal}
          forceDarkMode={forceDarkMode}
          isInOnboarding={renderLocation === 'onboarding'}
        />
      )}
      <MessagesSetupModal
        open={activeModal === 'apple-messages'}
        onClose={closeModal}
        trackConnection={trackConnection}
        forceDarkMode={forceDarkMode}
      />
      <PhoneSetupModal
        open={activeModal === 'apple-phone-log'}
        onClose={closeModal}
        trackConnection={trackConnection}
        forceDarkMode={forceDarkMode}
      />
    </>
  );
}

export const useOnUnsupportedPlatform = ({ setActiveModal, forceDarkMode, directUser }) => {
  return useCallback((provider: DeviceIntegrationProvider) => {
    switch (provider) {
      case 'linkedin':
        return () => setActiveModal('linkedin');
      case 'facebook':
        return promptForUnsupportedIntegration({
          integrationType: 'facebook',
          whereSupported: ['Mac', 'Windows', 'iOS'],
          docsLink: supportLinkSlugForDeviceIntegrationType['facebook'],
          directUser,
          forceDarkMode,
        });
      case 'twitter-platforms':
        return promptForUnsupportedIntegration({
          integrationType: 'twitter-platforms',
          whereSupported: ['Mac', 'Windows', 'iOS'],
          docsLink: supportLinkSlugForDeviceIntegrationType['twitter-platforms'],
          directUser,
          forceDarkMode,
        });
      case 'instagram':
        return promptForUnsupportedIntegration({
          integrationType: 'instagram',
          whereSupported: ['Mac', 'Windows', 'iOS'],
          docsLink: supportLinkSlugForDeviceIntegrationType['instagram'],
          directUser,
          forceDarkMode,
        });
      case 'whatsapp':
        return promptForUnsupportedIntegration({
          integrationType: 'whatsapp',
          whereSupported: ['Mac', 'Windows', 'iOS'],
          docsLink: supportLinkSlugForDeviceIntegrationType['whatsapp'],
          directUser,
          forceDarkMode,
        });
      case 'apple-phone-log':
        return promptForUnsupportedIntegration({
          integrationType: 'apple-phone-log',
          whereSupported: ['Mac'],
          docsLink: supportLinkSlugForDeviceIntegrationType['apple-phone-log'],
          directUser,
          forceDarkMode,
        });
      case 'apple-messages':
        return promptForUnsupportedIntegration({
          integrationType: 'apple-messages',
          whereSupported: ['Mac'],
          docsLink: supportLinkSlugForDeviceIntegrationType['apple-messages'],
          directUser,
          forceDarkMode,
        });
      default:
        return () => {};
    }
  }, []);
};

/*
classNames used for integration option styling in consumers:
  integration-option & add-integration-option | remove-integration-option
  device-integration-option & add-device-integration-option | remove-device-integration-option
  integration-option-icon
  device-integration-account-info
  integration-option-action-icon
*/

export const renderDeviceIntegrationOption = ({
  integrationProvider,
  isInOnboarding,
  setActiveModal,
  onUnsupported,
  className,
}) => {
  if (integrationIsIOSOnly(integrationProvider)) {
    return (
      <IOSOnlyIntegrationOption
        key="contacts"
        className={className}
        platform="contacts"
        isInOnboarding={isInOnboarding}
      />
    );
  }

  return (
    <DeviceIntegrationOption
      key={integrationProvider}
      className={className}
      platform={integrationProvider as DeviceIntegrationProvider}
      openModal={() => setActiveModal(integrationProvider)}
      onUnsupported={onUnsupported(integrationProvider as DeviceIntegrationProvider)}
      isInOnboarding={isInOnboarding}
    />
  );
};
